import React, {
  createContext,
  useContext,
  useMemo,
  PropsWithChildren
} from 'react';
import { Url } from '@atlassiansox/bxpkit-core';
import { ProductKey } from '@atlassiansox/bxpkit-core';
import {
  EDITION_LABEL_ARRAY,
  FREE_EDITION_LABEL_KEY,
  STANDARD_EDITION_LABEL_KEY,
  PREMIUM_EDITION_LABEL_KEY
} from '../utility';
import { ExperimentScreensFlags } from '../sign-up-layout-container';

export type Platform = 'ccp' | 'hams' | 'routing';
const PLATFORM_OPTIONS = ['ccp', 'hams', 'routing'] as Platform[];

export type SignUpFormConfig = {
  readonly bundle?: string;
  readonly product?: string;
  readonly edition?: string;
  readonly templateId?: string;
  readonly migrationSourceUuid?: string;
  readonly developer?: string;
  readonly src?: string;
  readonly useCcpPlatform: boolean;
  readonly platform: Platform;
  readonly experimentScreensFlags?: ExperimentScreensFlags;
  readonly customSignupContext?: string;
};

export type SignUpFormConfigContextProps = {
  readonly bundleOverride?: string;
  readonly productOverride?: string;
  readonly editionOverride?: string;
  readonly experimentScreensFlags?: ExperimentScreensFlags;
  readonly customSignupContext?: string;
};

export const SignUpConfigContext = createContext<SignUpFormConfigContextProps>(
  {}
);

export function SignUpConfigContextProvider({
  children,
  ...props
}: PropsWithChildren<SignUpFormConfigContextProps>) {
  return (
    <SignUpConfigContext.Provider value={props}>
      {children}
    </SignUpConfigContext.Provider>
  );
}

function buildSignUpFormConfig(
  bundleOverride?: string,
  productOverride?: string,
  editionOverride?: string,
  productParam?: string,
  bundleParam?: string,
  editionParam?: string,
  templateIdParam?: string,
  developerParam?: string,
  migrationSourceUuidParam?: string,
  srcParam?: string,
  platformParam?: string,
  experimentScreensFlags?: ExperimentScreensFlags,
  customSignupContext?: string
) {
  // figure out what the product key is
  let product;
  if (productOverride && productOverride.length > 0) {
    product = productOverride.replace(
      ProductKey.JIRA_SERVICE_MANAGEMENT,
      ProductKey.JIRA_SERVICE_DESK
    );
  } else if (productParam && productParam.length > 0) {
    product = productParam.replace(
      ProductKey.JIRA_SERVICE_MANAGEMENT,
      ProductKey.JIRA_SERVICE_DESK
    );
  }

  // figure out what the bundle name is
  let bundle;
  if (bundleOverride && bundleOverride.length > 0) {
    bundle = bundleOverride;
  } else if (bundleParam && bundleParam.length > 0) {
    bundle = bundleParam.split('.')[0];
  }

  // either product (product key), or bundle (bundle name), should exist, not both
  if (bundle && bundle.length) {
    product = undefined;
  } else if (product && product.length) {
    bundle = undefined;
  }
  // if nothing is provided, set bundle to 'default', which is Jira Software
  else {
    bundle = 'default';
  }

  // add edition data
  let edition;
  if (editionOverride && editionOverride.length > 0) {
    edition = EDITION_LABEL_ARRAY.some(
      (label) => label.toLowerCase() === editionOverride
    )
      ? editionOverride
      : FREE_EDITION_LABEL_KEY.toLowerCase();
  } else if (editionParam && editionParam.length > 0) {
    edition = EDITION_LABEL_ARRAY.some(
      (label) => label.toLowerCase() === editionParam
    )
      ? editionParam
      : FREE_EDITION_LABEL_KEY.toLowerCase();
  } else {
    edition = FREE_EDITION_LABEL_KEY.toLowerCase();
  }

  // adjust edition based on migrationSourceUuid query parameter
  // should either be standard or premium; always correct free to standard
  if (
    migrationSourceUuidParam &&
    migrationSourceUuidParam.length > 0 &&
    edition !== STANDARD_EDITION_LABEL_KEY.toLowerCase() &&
    edition !== PREMIUM_EDITION_LABEL_KEY.toLowerCase()
  ) {
    edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
  }

  // Developer Instances should always be Standard Edition
  if (developerParam) {
    edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
  }
  // SPICE-448: Prototype products - Point A alpha or beta products
  if (bundle === ProductKey.COMPASS || product === ProductKey.COMPASS) {
    // BXPSU-1499 Compass GA Launch - Add support for Free and Standard
    // No Compass Premium edition so instead gets set to Standard edition
    if (edition === PREMIUM_EDITION_LABEL_KEY.toLowerCase()) {
      edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
    }
  }

  // Bitbucket CMTs should always be Premium Edition
  if (
    !editionParam &&
    migrationSourceUuidParam &&
    migrationSourceUuidParam.length > 0 &&
    bundle === 'bitbucket'
  ) {
    edition = PREMIUM_EDITION_LABEL_KEY.toLowerCase();
  }

  let platform = 'routing' as Platform;
  let useCcpPlatform = false;
  if (platformParam) {
    const platformParamLowerCase = platformParam.toLowerCase() as Platform;
    if (platformParamLowerCase === 'ccp') {
      useCcpPlatform = true;
    }

    if (PLATFORM_OPTIONS.includes(platformParamLowerCase)) {
      platform = platformParamLowerCase;
    }
  }

  return {
    bundle,
    edition,
    product,
    migrationSourceUuid: migrationSourceUuidParam,
    templateId: templateIdParam,
    developer: developerParam,
    src: srcParam,
    useCcpPlatform,
    platform,
    experimentScreensFlags,
    customSignupContext
  };
}

export function useSignUpFormConfig(): SignUpFormConfig {
  const {
    bundleOverride,
    productOverride,
    editionOverride,
    experimentScreensFlags,
    customSignupContext
  } = useContext(SignUpConfigContext);

  const {
    product: productParam,
    bundle: bundleParam,
    edition: editionParam,
    templateId: templateIdParam,
    developer: developerParam,
    migrationSourceUuid: migrationSourceUuidParam,
    src: srcParam,
    platform: platformParam
  } = Url.getUrlParams() || {};

  return useMemo(
    () =>
      buildSignUpFormConfig(
        bundleOverride,
        productOverride,
        editionOverride,
        productParam,
        bundleParam,
        editionParam,
        templateIdParam,
        developerParam,
        migrationSourceUuidParam,
        srcParam,
        platformParam,
        experimentScreensFlags,
        customSignupContext
      ),
    [
      bundleOverride,
      productOverride,
      editionOverride,
      productParam,
      bundleParam,
      editionParam,
      templateIdParam,
      developerParam,
      migrationSourceUuidParam,
      srcParam,
      platformParam,
      experimentScreensFlags,
      customSignupContext
    ]
  );
}
